import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Grid,
  TextField,
  Select,
  InputLabel,
  FormControl,
  MenuItem,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import api from '../../globals/axios';
import CustomAppBar from '../../components/AppBar';
import GenericButton from '../../components/Custom/GenericButton';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../components/Alert';
import AdminDashboardCard from '../../components/Custom/AdminDashboardCard';
import { isNotNullOrEmptyArray } from '../../globals/utils';

const useStyles = makeStyles(() => ({
  root: {
    width: '100%',
    minHeight: '100vh',
    marginBottom: '-4rem',
  },
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  title: {
    textAlign: 'center',
    padding: '20px 0',
    borderRadius: '4px',
    marginBottom: '20px',
    border: '1px solid rgba(0, 0, 0, 0.12)',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'center',
    padding: '20px 0',
  },
  filterContainer: {
    display: 'flex',
    gap: '16px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  statsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
  },
}));

const SubscriptionInvoice = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { showAlert } = useAlert();
  const navigate = useNavigate();

  const [orderInvoices, setOrderInvoices] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    total_pages: 0,
    total_objects: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    name: '',
    phone_number: '',
    invoice_number: '',
    order_id: '',
    payment_mode: '',
  });
  const [stats, setStats] = useState({});

  const columns = [
    { key: 'farmer_name', label: 'Farmer Name' },
    { key: 'phone_number', label: 'Phone Number' },
    { key: 'invoice_number', label: 'Invoice Number' },
    { key: 'order_id', label: 'Order ID' },
    { key: 'payment_mode', label: 'Payment Mode' },
    { key: 'amount', label: 'Amount (₹)' },
    { key: 'created_at', label: 'Created At' },
    { key: 'action', label: 'Action' },
  ];

  const statsItems = [
    {
      label: t('Total Invoices'),
      value: stats.total_invoices || 0,
      color: '#FFEB3B',
    },
    {
      label: t('Farmers'),
      value: stats.total_farmers || 0,
      color: '#FF5722',
    },
    {
      label: t('Total Amount'),
      value: (stats.total_amount || 0.0).toFixed(2),
      color: '#00BCD4',
    },
    {
      label: t('Total Base Amount'),
      value: (stats.total_base_amount || 0.0).toFixed(2),
      color: '#8BC34A',
    },
    {
      label: t('Total GST Amount'),
      value: (stats.total_gst_amount || 0.0).toFixed(2),
      color: '#FFC107',
    },
  ];

  const fetchOrders = async (page = 1) => {
    setIsLoading(true);

    let { farmer_name, phone_number, invoice_number, order_id, payment_mode } =
      filters;
    let query = `?page=${page}`;
    if (farmer_name) query += `&farmer_name=${farmer_name}`;
    if (phone_number) query += `&phone_number=${phone_number}`;
    if (invoice_number) query += `&invoice_number=${invoice_number}`;
    if (order_id) query += `&order_id=${order_id}`;
    if (payment_mode) query += `&payment_mode=${payment_mode}`;

    try {
      const response = await api.get(
        `/app-admin/get-ca-farmer-subscriptions-invoices/${query}`
      );
      const responseData = response?.data;
      setOrderInvoices(responseData.results || []);
      setPagination({
        page: responseData.current_page_number,
        total_pages: responseData.total_pages,
        total_objects: responseData.total_objects,
      });

      const statsRes = await api.get(
        `/app-admin/get-ca-subscription-invoices-stats/`
      );
      if (statsRes?.data?.data) {
        setStats(statsRes.data.data);
      }
    } catch (error) {
      console.log(error);
      showAlert('error', 'Failed to fetch subscription invoices');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handlePageChange = (event, value) => {
    fetchOrders(value);
  };

  return (
    <Box className={classes.root} component={Paper}>
      <CustomAppBar />
      <Box className={classes.container}>
        <Paper className={classes.title} elevation={0}>
          <Typography variant="h5">Farmer Subscriptions Invoice</Typography>
        </Paper>

        <Box className={classes.statsContainer}>
          {statsItems.map((stat, index) => (
            <AdminDashboardCard
              key={index}
              label={stat.label}
              value={stat.value}
              color={stat.color}
            />
          ))}
        </Box>

        <Box className={classes.filterContainer}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={t('Farmer Name')}
                value={filters.farmer_name}
                onChange={e =>
                  setFilters({ ...filters, farmer_name: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={t('Phone Number')}
                value={filters.phone_number}
                onChange={e =>
                  setFilters({ ...filters, phone_number: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={t('Invoice Number')}
                value={filters.invoice_number}
                onChange={e =>
                  setFilters({ ...filters, invoice_number: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <TextField
                label={t('Order Id')}
                value={filters.order_id}
                onChange={e =>
                  setFilters({ ...filters, order_id: e.target.value })
                }
                fullWidth
              />
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth>
                <InputLabel id="payment-mode-label">
                  {t('Payment Mode')}
                </InputLabel>
                <Select
                  labelId="payment-mode-label"
                  value={filters.payment_mode || ''}
                  label={t('Payment Mode')}
                  onChange={e =>
                    setFilters({ ...filters, payment_mode: e.target.value })
                  }
                >
                  <MenuItem value="CASH">{t('CASH')}</MenuItem>
                  <MenuItem value="UPI">{t('UPI')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={2}>
              <GenericButton
                backgroundColor={'#4CAF50'}
                fontColor={'#fff'}
                fontSize={'14px'}
                fullWidth
                padding={'15px 10px'}
                onClick={() => fetchOrders(1)}
              >
                {t('Search')}
              </GenericButton>
            </Grid>
          </Grid>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map(col => (
                  <TableCell
                    key={col.key}
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#FFC107',
                      color: 'white',
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : !isNotNullOrEmptyArray(orderInvoices) ? (
                <TableRow>
                  <TableCell colSpan={columns.length} align="center">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                orderInvoices.map((invoice, idx) => (
                  <TableRow key={idx}>
                    {columns.map(col => (
                      <TableCell key={col.key} align="center">
                        {col.key === 'action' ? (
                          <GenericButton
                            backgroundColor={'#4CAF50'}
                            fontColor={'#fff'}
                            fontSize={'12px'}
                            padding={'5px 10px'}
                            onClick={() =>
                              navigate(
                                `/subscription-invoice-detail/${invoice.uuid}`
                              )
                            }
                          >
                            View Details
                          </GenericButton>
                        ) : col.key === 'amount' ? (
                          (invoice[col.key] || 0).toFixed(2)
                        ) : (
                          invoice[col.key] || 'N/A'
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box className={classes.pagination}>
          <Pagination
            count={pagination.total_pages}
            page={pagination.page}
            onChange={handlePageChange}
            color="primary"
            disabled={isLoading}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SubscriptionInvoice;
